/**
 * Role-based access control utilities for DZ Metrics Frontend
 */

// Platform (admin) role hierarchy - higher number = more permissions
export const PLATFORM_ROLE_HIERARCHY = {
  super_admin: 4,
  admin: 3,
  support_lead: 2,
  support_agent: 1,
  read_only: 0,
};

// Tenant (customer) role hierarchy
export const TENANT_ROLE_HIERARCHY = {
  tenant_admin: 2,
  member: 1,
};

// Combined hierarchy for validation
export const ROLE_HIERARCHY = {
  ...PLATFORM_ROLE_HIERARCHY,
  ...TENANT_ROLE_HIERARCHY,
};

/**
 * Get numeric level for a role
 * @param {string} role - The role to check
 * @returns {number} Role level or -1 if unknown
 */
export const getRoleLevel = (role) => {
  return ROLE_HIERARCHY[role] ?? -1;
};

/**
 * Check if user role meets minimum required role
 * @param {string} userRole - The user's current role
 * @param {string} requiredRole - The minimum required role
 * @returns {boolean} True if user has sufficient permissions
 */
export const hasMinimumRole = (userRole, requiredRole) => {
  const userLevel = getRoleLevel(userRole);
  const requiredLevel = getRoleLevel(requiredRole);
  return userLevel >= requiredLevel && requiredLevel >= 0;
};

/**
 * Check if user can access a route based on tier and role
 * @param {Object} user - The user object
 * @param {string|null} requiredTier - Required tier ('tenant' or 'platform')
 * @param {string|null} requiredRole - Minimum required role
 * @returns {boolean} True if user can access
 */
export const canAccessRoute = (user, requiredTier = null, requiredRole = null) => {
  if (!user) return false;

  // Check tier if specified
  if (requiredTier && user.tier !== requiredTier) {
    return false;
  }

  // Check role if specified
  if (requiredRole && !hasMinimumRole(user.role, requiredRole)) {
    return false;
  }

  return true;
};

/**
 * User tier checks
 */
export const isTenant = (user) => user?.tier === 'tenant';
export const isPlatform = (user) => user?.tier === 'platform';

/**
 * Tenant role checks
 */
export const isTenantAdmin = (user) => isTenant(user) && user?.role === 'tenant_admin';
export const isMember = (user) => isTenant(user) && user?.role === 'member';

/**
 * Platform role checks
 */
export const isSuperAdmin = (user) => isPlatform(user) && user?.role === 'super_admin';
export const isAdmin = (user) => isPlatform(user) && hasMinimumRole(user?.role, 'admin');
export const isSupport = (user) => isPlatform(user) && hasMinimumRole(user?.role, 'support_agent');

/**
 * Permission checks
 */
export const canManageTenant = (user) => {
  // Tenant admins can manage their own tenant
  if (isTenantAdmin(user)) return true;
  // Platform admins+ can manage tenants
  if (isPlatform(user) && isAdmin(user)) return true;
  return false;
};

export const canManageUsers = (user) => {
  // Only platform admins can manage users
  return isPlatform(user) && isAdmin(user);
};

export const canViewLogs = (user) => {
  // Admins and above can view logs
  return isPlatform(user) && isAdmin(user);
};

export const canManageSystem = (user) => {
  // Only super admins can manage system
  return isSuperAdmin(user);
};

/**
 * Get the appropriate home path based on user tier
 * @param {Object} user - The user object
 * @returns {string} Home path for the user
 */
export const getHomePath = (user) => {
  if (!user) return '/login';
  if (isPlatform(user)) return '/admin/dashboard';
  return '/dashboard';
};

/**
 * Get the appropriate login path based on intended tier
 * @param {string} tier - 'tenant' or 'platform'
 * @returns {string} Login path
 */
export const getLoginPath = (tier = 'tenant') => {
  return tier === 'platform' ? '/admin/login' : '/login';
};

export default {
  ROLE_HIERARCHY,
  PLATFORM_ROLE_HIERARCHY,
  TENANT_ROLE_HIERARCHY,
  getRoleLevel,
  hasMinimumRole,
  canAccessRoute,
  isTenant,
  isPlatform,
  isTenantAdmin,
  isMember,
  isSuperAdmin,
  isAdmin,
  isSupport,
  canManageTenant,
  canManageUsers,
  canViewLogs,
  canManageSystem,
  getHomePath,
  getLoginPath,
};
